interface ReportErrorOptions {
  source: string;
  error: unknown;
  context?: Record<string, unknown> | string;
}

const REPORT_FUNCTION_PATH = '/.netlify/functions/report-error';

function normalizeError(error: unknown): { message: string; stack?: string } {
  if (error instanceof Error) {
    return { message: error.message, stack: error.stack };
  }

  if (typeof error === 'string') {
    return { message: error };
  }

  try {
    const serialized = JSON.stringify(error);
    return { message: serialized };
  } catch (serializationError) {
    return {
      message: 'Unknown error object that could not be serialized',
      stack: serializationError instanceof Error ? serializationError.stack : undefined
    };
  }
}

/**
 * Stable-per-tab client session id, so repeated alerts from the same browser
 * session can be correlated. Stored in sessionStorage (cleared when the tab
 * closes); falls back gracefully where storage/crypto is unavailable.
 */
function getClientSessionId(): string | null {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return null;
    }
    const KEY = 'mm_client_session_id';
    let id = window.sessionStorage.getItem(KEY);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `sess_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      window.sessionStorage.setItem(KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

/** Environment/diagnostic fields attached to every admin alert. */
function collectClientDiagnostics(): Record<string, unknown> {
  const loc = typeof window !== 'undefined' ? window.location : undefined;
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  return {
    timestamp: new Date().toISOString(),
    url: loc ? loc.href : 'unknown',
    referrer: typeof document !== 'undefined' ? document.referrer || null : null,
    userAgent: nav ? nav.userAgent : 'unknown',
    language: nav ? nav.language : null,
    sessionId: getClientSessionId()
  };
}

export async function reportErrorToAdmin({ source, error, context }: ReportErrorOptions): Promise<void> {
  const { message, stack } = normalizeError(error);

  const payload = {
    source,
    message,
    stack,
    context,
    diagnostics: collectClientDiagnostics()
  };

  try {
    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    };

    const response = await fetch(REPORT_FUNCTION_PATH, requestInit);

    if (!response.ok) {
      console.error('[reportErrorToAdmin] Failed to deliver alert', response.status, response.statusText);
    }
  } catch (reportError) {
    console.error('[reportErrorToAdmin] Unexpected failure while reporting error', reportError);
  }
}
